import React, { useEffect, useRef, useState } from "react";
import { FileDown, Loader2, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ApprovalsList from "./inbox/ApprovalsList";
import { Dialog } from "./inbox/InboxDialogs";

const DOC = "protocole_desinstallation";

function SignDialog({ dossier, onClose, onSent }) {
  const ex = dossier.extraction || {};
  const [f, setF] = useState({ first_name: ex.prenom || "", last_name: ex.nom || "", email: ex.email || "", phone: ex.telephone || "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/dossiers/${dossier.id}/signatures`, { doc_id: DOC, ...f }); toast.success("Protocole envoyé à signer (Yousign)"); onSent(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Envoyer le protocole de désinstallation (Yousign)" onClose={onClose} testid="desinst-yousign-dialog"
      footer={<><button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
        <button onClick={send} disabled={busy} className="btn-primary text-sm" data-testid="desinst-yousign-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer</button></>}>
      <div className="grid grid-cols-2 gap-2">
        {[["first_name", "Prénom"], ["last_name", "Nom"], ["email", "Email"], ["phone", "Téléphone (code SMS, facultatif)"]].map(([k, l]) => (
          <label key={k} className="block space-y-1 text-[11px] text-norea-textMuted">{l}
            <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-field text-sm w-full" data-testid={`desinst-yousign-${k}`} /></label>
        ))}
      </div>
    </Dialog>
  );
}

export default function DesinstallationPanel({ dossier, onChanged }) {
  const [ys, setYs] = useState(null);
  const [sigs, setSigs] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [key, setKey] = useState(0);
  const prev = useRef(null);
  useEffect(() => { const t = setInterval(() => setKey((k) => k + 1), 8000); return () => clearInterval(t); }, []);
  const onCount = (n) => { if (prev.current !== null && n < prev.current) onChanged?.(); prev.current = n; };
  const load = () => api.get(`/dossiers/${dossier.id}/signatures`).then((r) => { setYs(r.data.configured); setSigs(r.data.items.filter((s) => s.doc_id === DOC)); }).catch(() => setYs(false));
  useEffect(() => { load(); }, [dossier.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const print = async () => {
    try { const r = await api.get(`/dossiers/${dossier.id}/documents/${DOC}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Génération impossible (informations manquantes ?)"); }
  };
  return (
    <section className="card-flat p-5 mb-4 space-y-4" data-testid="desinst-panel">
      <div>
        <h3 className="font-heading text-base font-medium inline-flex items-center gap-2"><Undo2 className="w-4 h-4 text-norea-primary" />Protocole d'accord de désinstallation</h3>
        <p className="text-[11px] text-norea-textMuted mt-0.5">Le statut avance tout seul : protocole envoyé (Yousign) → signé (Yousign ou scan déposé) ; « Désinstallation réalisée » prépare l'email d'annulation au financeur ; le dépôt de la confirmation d'annulation prépare l'email au client. Tous les emails attendent dans « À approuver » (simulation : rien ne part).</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={print} className="btn-secondary text-xs" data-testid="desinst-protocole-pdf"><FileDown className="w-3.5 h-3.5" /> Protocole à imprimer</button>
        <button onClick={() => setDlg(true)} disabled={!ys} title={ys ? "" : "Yousign pas encore connecté"} className="btn-secondary text-xs" data-testid="desinst-protocole-yousign"><Send className="w-3.5 h-3.5" /> Envoyer par Yousign</button>
      </div>
      {ys === false && <div className="text-[11px] text-norea-textMuted" data-testid="desinst-yousign-off">Yousign pas encore connecté (clé à saisir dans Configuration → Connexions) : imprimez le protocole, faites-le signer puis déposez le scan dans « Protocole signé » ci-dessous.</div>}
      {sigs.map((s) => <div key={s.id} className="text-[11px] text-norea-textMuted" data-testid={`desinst-signature-${s.id}`}>Yousign · {s.signer?.email} · <strong>{s.status_label}</strong></div>)}
      <div>
        <div className="text-xs font-medium mb-1">Emails à approuver</div>
        <ApprovalsList dossierId={dossier.id} tab="desinstallation" refreshKey={key} onCount={onCount} />
      </div>
      {dlg && <SignDialog dossier={dossier} onClose={() => setDlg(false)} onSent={() => { load(); setKey((k) => k + 1); onChanged?.(); }} />}
    </section>
  );
}
